import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Check, Wind, MessageCircle, PhoneCall, ArrowRight, CircleDot } from "lucide-react";
import { WEEKS } from "@/lib/mentalPlan";

const INK = "#2E2640";
const SOFT = "rgba(46,38,64,0.62)";

/* what a person already inside the programme sees below their path —
   nothing from the entrance is repeated here. */

function Ladder({ hue, doneCount }) {
  const openWeeks = 1 + Math.floor(doneCount / 7);
  const [on, setOn] = useState(0);
  const w = WEEKS[on];
  return (
    <section className="relative" style={{ background: "linear-gradient(180deg, #F7F3FB, #F0E9FA)" }} data-testid="inprog-ladder">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
        <div className="flex flex-wrap items-end justify-between gap-5 mb-9">
          <div>
            <p className="text-[10px] tracking-[0.24em] uppercase" style={{ color: "rgba(46,38,64,0.4)" }}>Where this goes next</p>
            <h2 className="font-display text-[32px] sm:text-[44px] leading-[1.02] mt-2 max-w-md" style={{ color: INK }}>Week {openWeeks} is open. The rest arrives as you close days.</h2>
          </div>
          <div className="text-right">
            <p className="font-display text-[52px] leading-none" style={{ color: hue }}>{doneCount}</p>
            <p className="text-[11px] tracking-[0.18em] uppercase mt-1" style={{ color: "rgba(46,38,64,0.4)" }}>days closed</p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
          {WEEKS.map((x, i) => {
            const unlocked = x.n <= openWeeks;
            const active = on === i;
            return (
              <button key={x.n} onClick={() => setOn(i)} className="text-left rounded-2xl px-4 py-5 transition-all hover:-translate-y-1"
                style={active
                  ? { background: "#fff", border: `1px solid ${hue}55`, boxShadow: "0 26px 50px -38px rgba(46,38,64,0.8)" }
                  : { background: unlocked ? "rgba(255,255,255,0.6)" : "rgba(255,255,255,0.32)", border: "1px solid rgba(46,38,64,0.06)" }}
                data-testid={`inprog-week-${x.n}`}>
                <div className="flex items-center gap-2">
                  <p className="font-display text-[19px] tabular-nums" style={{ color: unlocked ? hue : "rgba(46,38,64,0.3)" }}>{String(x.n).padStart(2, "0")}</p>
                  {unlocked ? <Check size={12} style={{ color: hue, opacity: 0.7 }} /> : <Lock size={11} style={{ color: "rgba(46,38,64,0.28)" }} />}
                </div>
                <p className="text-[12.5px] mt-2 leading-snug" style={{ color: unlocked ? INK : "rgba(46,38,64,0.42)" }}>{x.title}</p>
              </button>
            );
          })}
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={on} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.32 }}
            className="mt-5 rounded-[1.6rem] px-6 py-6" style={{ background: "#fff", border: "1px solid rgba(46,38,64,0.07)" }}>
            <p className="text-[10px] tracking-[0.2em] uppercase" style={{ color: hue }}>week {w.n} · {w.title}</p>
            <p className="font-display text-[22px] sm:text-[26px] mt-2 leading-snug" style={{ color: INK }}>{w.line}</p>
            {w.n > openWeeks && (
              <p className="text-[13px] mt-3" style={{ color: SOFT }}>Opens after {(w.n - 1) * 7 - doneCount} more closed day{(w.n - 1) * 7 - doneCount === 1 ? "" : "s"}. Nothing here is on a calendar; it waits for you.</p>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}

function Heavy({ hue, onOpenDay }) {
  const [breath, setBreath] = useState(false);
  return (
    <section className="relative overflow-hidden" style={{ background: "linear-gradient(165deg, #3A2F5C, #241C40 72%)" }} data-testid="inprog-heavy">
      <span className="absolute right-[8%] -top-24 w-[32vw] h-[32vw] rounded-full halo" style={{ background: "radial-gradient(circle, rgba(167,139,250,0.4), transparent 68%)", filter: "blur(40px)" }} />
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-20">
        <div className="max-w-xl mb-9">
          <p className="text-[10px] tracking-[0.24em] uppercase text-white/45">For the days it does not go to plan</p>
          <h2 className="font-display text-[32px] sm:text-[44px] leading-[1.02] mt-2 text-white">If today is heavy, do the smallest thing</h2>
          <p className="text-[15px] mt-3 text-white/55">Missing a day is part of this, not a failure of it. Pick one of these three instead and the path stays intact.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="rounded-[1.6rem] p-6 relative overflow-hidden" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }} data-testid="heavy-breath">
            <Wind size={16} style={{ color: "#E8B84B" }} />
            <p className="font-display text-[21px] mt-3 text-white leading-snug">Sixty seconds of breath</p>
            <p className="text-[13px] mt-2 text-white/55">In for four, out for six. That is the whole ask.</p>
            <button onClick={() => setBreath(!breath)} className="mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-semibold"
              style={breath ? { background: "rgba(255,255,255,0.14)", color: "#fff" } : { background: "#E8B84B", color: "#2E2640" }} data-testid="heavy-breath-toggle">
              {breath ? "stop" : "start"} <ArrowRight size={12} />
            </button>
            <motion.span className="absolute -right-8 -bottom-8 w-32 h-32 rounded-full" style={{ background: "radial-gradient(circle, rgba(232,184,75,0.4), transparent 70%)" }}
              animate={breath ? { scale: [0.8, 1.25, 0.8], opacity: [0.4, 0.9, 0.4] } : { scale: 0.9, opacity: 0.3 }} transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }} />
          </div>

          <div className="rounded-[1.6rem] p-6" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }} data-testid="heavy-day">
            <CircleDot size={16} style={{ color: "#A78BFA" }} />
            <p className="font-display text-[21px] mt-3 text-white leading-snug">Open today, read only the first part</p>
            <p className="text-[13px] mt-2 text-white/55">You are allowed to close it after two minutes. It still counts.</p>
            <button onClick={onOpenDay} className="mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-semibold" style={{ background: "#fff", color: "#2E2640" }} data-testid="heavy-open-day">
              Open it <ArrowRight size={12} />
            </button>
          </div>

          <div className="rounded-[1.6rem] p-6" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }} data-testid="heavy-reach">
            <MessageCircle size={16} style={{ color: "#8DE3B4" }} />
            <p className="font-display text-[21px] mt-3 text-white leading-snug">Say one true sentence to one person</p>
            <p className="text-[13px] mt-2 text-white/55">“Today has been heavy and I did not want to pretend otherwise.” Copy it if it helps.</p>
            <button onClick={() => navigator.clipboard?.writeText("Today has been heavy and I did not want to pretend otherwise.")}
              className="mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-semibold" style={{ background: "rgba(255,255,255,0.14)", color: "#fff" }} data-testid="heavy-copy">
              Copy the sentence
            </button>
          </div>
        </div>

        <div className="mt-4 rounded-[1.6rem] px-6 py-5 flex flex-wrap items-center gap-4" style={{ background: "rgba(226,133,122,0.14)", border: "1px solid rgba(226,133,122,0.3)" }} data-testid="inprog-crisis">
          <PhoneCall size={16} style={{ color: "#F0A79E" }} />
          <p className="text-[14px] text-white/80 flex-1">If it is worse than heavy, the crisis numbers sit in the top bar of every page, on every platform, at every hour.</p>
        </div>
      </div>
    </section>
  );
}

export default function InProgramme({ hue, doneCount, onOpenDay }) {
  return (
    <>
      <Ladder hue={hue} doneCount={doneCount} />
      <Heavy hue={hue} onOpenDay={onOpenDay} />
    </>
  );
}
